import React from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { rem } from 'polished';
import { Text } from '../Text';

interface StyledTechThumbnailProps {
  hide: boolean;
}

const StyledTechThumbnail = styled.div<StyledTechThumbnailProps>`
  visibility: ${({ hide }) => (hide ? `hidden` : `inherit`)};
  display: flex;
  flex-direction: column;
  position: relative;
  border-radius: ${rem(`12px`)};
  overflow: hidden;
  margin-right: 0;
  margin-bottom: ${rem(`28px`)};
  transform: scale(1);
  transition: box-shadow 0.8s, transform 0.6s;
  flex: 1 0 auto;
  width: 100%;
  ${({ theme }) => theme.breakpoints.desktop`
    &:hover {
      cursor: pointer;
      transform: scale(1.1);
      box-shadow: 0px 15px 20px -14px rgba(246, 56, 251, 0.5), 0px -15px 20px -14px rgba(246, 56, 251, 0.5);
      .tech-thumbnail__wrapper::after {
        opacity: 1;
        transform: rotate(-45deg) translateY(100%);
      }
    }
  `}
  .tech-thumbnail__wrapper {
    display: flex;
    flex: 1 0 100%;
    flex-wrap: wrap;
    padding: 1px;
    margin: 1px;
    border-radius: ${rem(`12px`)};
    position: relative;
    box-sizing: border-box;
    background-clip: padding-box;
    border: solid 1px transparent;
    transition: all 0.5s ease;
    background: ${({ theme }) => `linear-gradient(
      265deg,
      ${theme.colors.blue_1_65},
      ${theme.colors.blue_1},
      ${theme.colors.blue_1_90}
    )`};
    ::before {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      border-radius: inherit;
      margin: -2px;
      z-index: -1;
      background: ${({ theme }) => `linear-gradient(
          -30deg,
          ${theme.colors.blue_1_30},
          ${theme.colors.blue_1_65},
          ${theme.colors.pink_2}
        )`};
    }
    ::after {
      content: '';
      position: absolute;
      top: -110%;
      left: -110%;
      width: 325%;
      height: 325%;
      background: linear-gradient(
        0deg,
        transparent,
        transparent 30%,
        rgba(246, 56, 251, 0.3)
      );
      transform: rotate(-45deg);
      transition: all 0.5s ease;
      opacity: 0;
    }
  }
  .tech-thumbnail__container {
    padding: ${rem(`6px`)};
    padding-right: ${rem(`40px`)};
    display: flex;
    flex: 0 1 auto;
    position: relative;
  }
  .tech-thumbnail__image-wrapper {
    display: flex;
    flex-wrap: wrap;
    border-radius: ${rem(`6px`)};
    overflow: hidden;
    width: ${rem(`60px`)};
    height: ${rem(`60px`)};
  }
  .tech-thumbnail__image {
    align-items: center;
    justify-content: center;
    position: relative;
  }
  .tech-thumbnail__text {
    display: flex;
    color: ${({ theme }) => theme.colors.white_1};
    margin-left: ${rem(`12px`)};
    align-items: center;
    justify-content: flex-start;
    z-index: 99;
    text-align: left;
    span {
      line-height: ${rem(`24px`)};
    }
  }
  .tech-thumbnail__right-container {
    padding: ${rem(`6px`)};
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    align-items: center;
    position: absolute;
    right: 0;
    top: 0;
    z-index: 99;
    height: ${rem(`76px`)};
  }
  .tech-thumbnail__right {
  }
`;

export interface ITechThumbnail {
  className?: string;
  title: string;
  image: any;
  alt: string;
  hide?: boolean;
  rightSide: React.ReactElement[];
}

export const TechThumbnail = ({
  className,
  title,
  image,
  alt,
  hide,
  rightSide,
}: ITechThumbnail) => (
  <StyledTechThumbnail className={className} hide={hide}>
    <div className="tech-thumbnail__wrapper">
      <div className="tech-thumbnail__container">
        <div className="tech-thumbnail__image-wrapper">
          <GatsbyImage
            image={image}
            alt={alt}
            className="tech-thumbnail__image"
          />
        </div>
        <div className="tech-thumbnail__text">
          <Text noRhythem type="body1" tag="span">
            {title}
          </Text>
        </div>
      </div>
      <div className="tech-thumbnail__right-container">
        {rightSide.map((rightComponent) => (
          <div key={uuidv4()} className="tech-thumbnail__right">
            {rightComponent}
          </div>
        ))}
      </div>
    </div>
  </StyledTechThumbnail>
);
